import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Drawer,
  MobileStepper,
  Chip,
  TextField,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonIcon from '@mui/icons-material/Person';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { AiGradientButton } from '../shared';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import BookingDetailsStep from './steps/BookingDetailsStep';
import GuestDetailsStep from './steps/GuestDetailsStep';
import ReviewStep from './steps/ReviewStep';
import WalkInAiCheckinOverlay from './WalkInAiCheckinOverlay';
import BookingConfirmedActionsContent from './booking/BookingConfirmedActionsContent';
import BookingConfirmedSummaryCard from './booking/BookingConfirmedSummaryCard';
import CompanyDetailsHeadingActions from './booking/CompanyDetailsHeadingActions';
import WalkInMobileStepBar from './booking/WalkInMobileStepBar';
import DashboardHeader from './common/DashboardHeader';
import { useSyncedTheme } from '../hooks/useSyncedTheme';
import { resolveDashboardThemeMode } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getReservationShellSx,
  getReservationOuterPaperSx,
  getReservationFormPaperSx,
  getReservationFooterSx,
  getReservationFooterButtonSx,
  getReservationHeadingActionsSx,
} from '../utils/reservationMobileLayout';
import { scrollMobileStepTopIntoView } from '../utils/walkInMobileLayout';
import { computeAmountDueForPaymentLink } from '../utils/bookingPaymentLink';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';
import { getBookingCurrencySymbol } from '../utils/bookingPaymentSummary';
import { useBookingPaymentLinkComm } from '../hooks/useBookingPaymentLinkComm';
import {
  getAvailableRoomTypes,
  submitReservation,
  getInventorySlots,
  getTaxRulesAvailability,
  downloadBookingInvoice,
  downloadBookingConfirmation,
  sendBookingConfirmation,
  searchGuestByPhone,
  getCustomerDetail,
} from '../services/api';
import { formatWhatsAppSuccessMessage } from '../utils/whatsappMessaging';
import countriesData from '../data/countries-states-cities-data';
import countryCodesData from '../data/country-codes.json';
import AlertDialog from './common/AlertDialog';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../utils/locationSearch';
import { normalizeDocumentIdForSubmit, normalizeDocumentIdInput } from '../utils/documentIdFormatters';
import { isValidPhoneForCountry } from '../utils/phoneValidation';
import { calculateBookingPriceBreakdown } from '../utils/bookingPriceCalculation';
import { isAutoGstEnabled } from '../utils/autoGst';
import { toPaymentProcessedAtIso } from '../utils/dateUtils';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  foreignDetailsToGuestApiPayload,
  isForeignGuestCountry,
} from '../utils/foreignGuestFormUtils';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  createBookingDrawerOverlayProps,
} from '../utils/bookingDrawerLayout';

// Valid dial codes only — country code must come from dropdown, never from phone input
const VALID_DIAL_CODES = new Set(countryCodesData.map((cc) => cc.dialCode));

const EMPTY_COMPANY_DETAILS = {
  taxIdType: 'gstin',
  taxIdNumber: '',
  companyName: '',
  companyPhone: '',
  companyEmail: '',
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyCountry: '',
  companyPincode: '',
};

const hasAnyCompanyFieldValues = (details) =>
  Boolean(
    details &&
      (
        details.taxIdNumber ||
        details.companyName ||
        details.companyPhone ||
        details.companyEmail ||
        details.companyAddress ||
        details.companyCity ||
        details.companyState ||
        details.companyCountry ||
        details.companyPincode
      )
  );

const isCompanyFormEffectivelyEmpty = (details) => !hasAnyCompanyFieldValues(details);

const toCurrencyAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;
const getSelectedHotelCurrency = () =>
  (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.currency) || 'INR';

/** Nightly stay length in nights from calendar dates (checkout day exclusive). Raw moment.diff can show one fewer night when pickers attach different times (e.g. noon vs midnight). */
const getNightlyCalendarNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut || !checkIn.isValid() || !checkOut.isValid()) return 0;
  return Math.max(0, checkOut.clone().startOf('day').diff(checkIn.clone().startOf('day'), 'days'));
};

const STEPS = {
  BOOKING_DETAILS: 0,
  GUEST_DETAILS: 1,
  REVIEW: 2,
};

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
};

const RESERVATION_STEP_ICONS = [EventAvailableIcon, PersonIcon, FactCheckIcon];

const ReservationForm = () => {
  const theme = useTheme();
  const currentTheme = useSyncedTheme();
  const { can } = usePermission();
  const canManageAiCheckin = can(PERMISSIONS.AI_CHECKIN_MANAGE);
  const isMobileLayout = isMobileShellLayout();
  const navigate = useNavigate();
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const [activeStep, setActiveStep] = useState(STEPS.BOOKING_DETAILS);
  const [checkInDate, setCheckInDate] = useState(moment());
  const [checkOutDate, setCheckOutDate] = useState(moment().add(1, 'days'));
  const [rooms, setRooms] = useState([]);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  
  // Guest details - Name fields
  const [guestTitle, setGuestTitle] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestMiddleName, setGuestMiddleName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  // Guest details - Contact
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestPhoneCountryCode, setGuestPhoneCountryCode] = useState('+91'); // Default: India
  const [guestAlternatePhone, setGuestAlternatePhone] = useState('');
  const [guestAlternatePhoneCountryCode, setGuestAlternatePhoneCountryCode] = useState('+91'); // Default: India

  // Country code only from dropdown — ignore any value not in VALID_DIAL_CODES (e.g. +913 from phone input bug)
  const handleGuestPhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) {
      setGuestPhoneCountryCode(newCode);
    }
  }, []);
  const handleGuestAlternatePhoneCountryCodeChange = useCallback((newCode) => {
    if (VALID_DIAL_CODES.has(newCode)) {
      setGuestAlternatePhoneCountryCode(newCode);
    }
  }, []);
  // Guest details - Address
  const [guestAddress, setGuestAddress] = useState('');
  const [guestCity, setGuestCity] = useState('');
  const [guestState, setGuestState] = useState('');
  const [guestCountry, setGuestCountry] = useState('');
  const [guestPincode, setGuestPincode] = useState('');
  // Guest details - Personal
  const [guestDOB, setGuestDOB] = useState('');
  const [guestGender, setGuestGender] = useState('');
  // Guest details - Other
  const [guestETA, setGuestETA] = useState('');
  const [guestNote, setGuestNote] = useState('');
  const [bookingReference, setBookingReference] = useState('');
  const [bookingSource, setBookingSource] = useState('walk_in'); // Default: Walk-In
  const [remarks, setRemarks] = useState('');
  // Guest details - Documents
  const [uploadDocumentType, setUploadDocumentType] = useState('no_document'); // Default: No Document
  const [documentIdNumber, setDocumentIdNumber] = useState('');
  const [passportFrontId, setPassportFrontId] = useState('');
  const [passportBackId, setPassportBackId] = useState('');
  const [purposeOfStay, setPurposeOfStay] = useState('');
  const [purposeOfStayNote, setPurposeOfStayNote] = useState('');
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const hotelCountry =
    (typeof window !== 'undefined' &&
      ((window.DJANGO_CONTEXT || window.djangoContext)?.selectedHotel?.country ||
        (window.DJANGO_CONTEXT || window.djangoContext)?.hotel?.country)) ||
    '';
  
  // Booking type: 'nightly' or 'hourly'
  const [bookingType, setBookingType] = useState('nightly');
  
  // Review settings
  const autoGstEnabled = isAutoGstEnabled();
  const [gstIncluded, setGstIncluded] = useState(false);
  const effectiveGstIncluded = autoGstEnabled || gstIncluded;
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [allowDiscount, setAllowDiscount] = useState(false);
  const [discountMode, setDiscountMode] = useState('percentage');
  const [discountPercentage, setDiscountPercentage] = useState(5);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [allowExtraCharges, setAllowExtraCharges] = useState(false);
  const [extraChargesAmount, setExtraChargesAmount] = useState(0);
  const [extraChargesNote, setExtraChargesNote] = useState('');
  const [extraChargesItems, setExtraChargesItems] = useState([]);
  const [extraChargesBaseAmount, setExtraChargesBaseAmount] = useState(0);
  const [extraChargesGstAmount, setExtraChargesGstAmount] = useState(0);
  const [hasTaxRules, setHasTaxRules] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(0);
  const [taxRules, setTaxRules] = useState([]);
  
  // Collect Advance settings
  const [collectAdvance, setCollectAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [collectionDate, setCollectionDate] = useState(moment());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [advanceDocument, setAdvanceDocument] = useState('');
  
  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [activeBookingSlide, setActiveBookingSlide] = useState(0);
  const [successActionLoading, setSuccessActionLoading] = useState(null);
  const [successPaymentLinkBookingId, setSuccessPaymentLinkBookingId] = useState(0);
  const [successPaymentLinkAmount, setSuccessPaymentLinkAmount] = useState(0);
  const [showPaymentLinkOnSuccess, setShowPaymentLinkOnSuccess] = useState(false);
  
  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('info');
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Guest details step validation state
  const [isGuestDetailsValid, setIsGuestDetailsValid] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const validateGuestDetailsRef = useRef(null);
  const submitIdempotencyKeyRef = useRef(null);
  const reservationShellRef = useRef(null);
  const guestDetailsTopRef = useRef(null);
  
  // Show alert dialog
  const showAlert = (message, title = 'Alert', severity = 'warning') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertDialogOpen(false);
  };

  const successPaymentLinkBooking = useMemo(
    () => ({
      guest_email: guestEmail || '',
      guest_phone: `${String(guestPhoneCountryCode || '').replace(/\s/g, '')}${String(guestPhone || '').replace(/\D/g, '')}`,
      guest_name: [guestTitle, guestFirstName, guestMiddleName, guestLastName].filter(Boolean).join(' ').trim(),
    }),
    [
      guestEmail,
      guestPhone,
      guestPhoneCountryCode,
      guestTitle,
      guestFirstName,
      guestMiddleName,
      guestLastName,
    ]
  );

  const handleCheckInDateChange = useCallback(
    (newValue) => {
      if (!newValue || !newValue.isValid()) return;

      setCheckInDate(newValue);

      // For reservation nightly flow, always keep checkout as next day of selected check-in.
      if (bookingType === 'nightly') {
        setCheckOutDate(newValue.clone().add(1, 'day'));
      }
    },
    [bookingType]
  );
  
  // Room drawer state
  const [roomDrawerOpen, setRoomDrawerOpen] = useState(false);
  const [roomFormData, setRoomFormData] = useState({
    roomTypeId: '',
    mealPlan: 'EP',
    rooms: 0,
    adults: 0,
    children: 0,
    ratePerNight: 0,
    extraAdultCharge: 0,
    extraChildCharge: 0,
  });

  // Corporate/company details (same flow as walk-in)
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(EMPTY_COMPANY_DETAILS);
  const [companyDetailsSaved, setCompanyDetailsSaved] = useState(false);
  const [companyErrors, setCompanyErrors] = useState({});
  const [aiCheckinOpen, setAiCheckinOpen] = useState(false);
  const companyAutofillRequestRef = useRef(0);
  /** Company data from guest profile — applied only when user opens "Add Company Details". */
  const [guestProfileCompanySnapshot, setGuestProfileCompanySnapshot] = useState(null);
  const companyDetailsSavedRef = useRef(false);

  useEffect(() => {
    companyDetailsSavedRef.current = companyDetailsSaved;
  }, [companyDetailsSaved]);

  // Calculate remaining inventory for each room type based on already added rooms
  // Only counts rooms with overlapping date ranges
  const calculateRemainingInventory = useCallback(() => {
    if (!availableRoomTypes.length || !checkInDate || !checkOutDate) return availableRoomTypes;

    // Current date range for validation
    const currentCheckIn = checkInDate.format('YYYY-MM-DD');
    const currentCheckOut = checkOutDate.format('YYYY-MM-DD');

    // Helper function to check if date ranges overlap
    const dateRangesOverlap = (start1, end1, start2, end2) => {
      return start1 < end2 && start2 < end1;
    };

    // Count rooms already added by room type ID for overlapping dates only
    const roomsCountByType = {};
    rooms.forEach(room => {
      const roomTypeId = room.roomTypeId;
      if (roomTypeId && room.checkInDate && room.checkOutDate) {
        // Check if this room's dates overlap with current selection
        if (dateRangesOverlap(room.checkInDate, room.checkOutDate, currentCheckIn, currentCheckOut)) {
          roomsCountByType[roomTypeId] = (roomsCountByType[roomTypeId] || 0) + room.rooms;
        }
      }
    });

    // Update available_rooms for each room type
    return availableRoomTypes.map(roomType => {
      const usedRooms = roomsCountByType[roomType.id.toString()] || 0;
      const remainingRooms = Math.max(0, (roomType.available_rooms || 0) - usedRooms);
      
      return {
        ...roomType,
        original_available_rooms: roomType.original_available_rooms || roomType.available_rooms,
        available_rooms: remainingRooms,
        is_fully_booked: remainingRooms === 0,
      };
    });
  }, [availableRoomTypes, rooms, checkInDate, checkOutDate]);

  // Get filtered room types with remaining inventory
  const filteredRoomTypes = useMemo(() => {
    return calculateRemainingInventory();
  }, [calculateRemainingInventory]);

  // Calculate nights (nightly: by calendar day; hourly: whole-day diff if range spans days)
  const nights =
    checkOutDate && checkInDate
      ? bookingType === 'nightly'
        ? getNightlyCalendarNights(checkInDate, checkOutDate)
        : Math.max(0, checkOutDate.diff(checkInDate, 'days'))
      : 0;

  // Fetch available room types (for nightly booking) or inventory slots (for hourly booking)
  const fetchAvailableRoomTypes = useCallback(async () => {
    if (!checkInDate || !checkOutDate) {
      return;
    }

    setLoadingRoomTypes(true);
    try {
      const checkInDateStr = checkInDate.format('YYYY-MM-DD');
      const checkOutDateStr = checkOutDate.format('YYYY-MM-DD');
      
      if (bookingType === 'hourly') {
        // For hourly booking, fetch inventory slots
        const response = await getInventorySlots(checkInDateStr, checkOutDateStr);// Extract unique room types from inventory slots
        const slots = response.slots || [];
        const uniqueRoomTypes = {};
        slots.forEach(slot => {
          if (slot.room_type && !uniqueRoomTypes[slot.room_type.id]) {
            uniqueRoomTypes[slot.room_type.id] = {
              ...slot.room_type,
              slots: [],
            };
          }
          if (slot.room_type) {
            uniqueRoomTypes[slot.room_type.id].slots.push({
              id: slot.id,
              start_ts: slot.start_ts,
              end_ts: slot.end_ts,
              available_rooms: slot.available_rooms || 0,
            });
          }
        });
        // Convert to array format similar to nightly booking
        const roomTypesArray = Object.values(uniqueRoomTypes).map(rt => ({
          ...rt,
          available_rooms: Math.max(...rt.slots.map(s => s.available_rooms || 0), 0),
        }));
        setAvailableRoomTypes(roomTypesArray);
      } else {
        // For nightly booking, fetch room types as before
        const response = await getAvailableRoomTypes(checkInDateStr, checkOutDateStr);setAvailableRoomTypes(response.room_types || []);
      }
    } catch (error) {} finally {
      setLoadingRoomTypes(false);
    }
  }, [checkInDate, checkOutDate, bookingType]);

  // Fetch room types when dates or booking type change
  useEffect(() => {
    if (checkInDate && checkOutDate && checkOutDate > checkInDate) {
      fetchAvailableRoomTypes();
    }
  }, [checkInDate, checkOutDate, bookingType, fetchAvailableRoomTypes]);

  // Fetch tax rules availability on component mount
  useEffect(() => {
    const fetchTaxRulesAvailability = async () => {
      try {
        const data = await getTaxRulesAvailability('INR');
        setHasTaxRules(data.has_tax_rules || false);
        setTaxRules(Array.isArray(data.tax_rules) ? data.tax_rules : []);
        if (data.tax_percentage) {
          setGstPercentage(data.tax_percentage);
        }
      } catch (error) {setHasTaxRules(false);
        setGstPercentage(0);
        setTaxRules([]);
      }
    };
    fetchTaxRulesAvailability();
  }, []);

  // Auto-populate country from hotel context
  useEffect(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    const selectedHotel = djangoContext.selectedHotel || null;
    if (selectedHotel && selectedHotel.country) {
      // Hotel country might be a code (e.g., "IN") or name (e.g., "India")
      // Convert to country name if it's a code
      const hotelCountryValue = selectedHotel.country.trim();
      let countryName = hotelCountryValue;
      
      // Check if it's a country code or name by looking it up in countries data
      // Try exact match first (case-sensitive)
      let countryObj = countriesData.countries.find(c => 
        c.code === hotelCountryValue || c.name === hotelCountryValue
      );
      
      // If not found, try case-insensitive match
      if (!countryObj) {
        countryObj = countriesData.countries.find(c => 
          c.code?.toLowerCase() === hotelCountryValue.toLowerCase() || 
          c.name?.toLowerCase() === hotelCountryValue.toLowerCase()
        );
      }
      
      if (countryObj && countryObj.name) {
        // Use the full country name
        countryName = countryObj.name;
      }
      
      setGuestCountry(countryName);
    }
  }, []);

  // Look up guest by phone: store company data as snapshot only (do not auto-apply to this booking).
  useEffect(() => {
    const phoneDigits = (guestPhone || '').replace(/\D/g, '');
    if (!isValidPhoneForCountry(phoneDigits, guestPhoneCountryCode)) {
      companyAutofillRequestRef.current += 1;
      if (!companyDetailsSavedRef.current) {
        setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
        setCompanyDetailsSaved(false);
        setCompanyErrors({});
      }
      setGuestProfileCompanySnapshot(null);
      return undefined;
    }

    const requestId = ++companyAutofillRequestRef.current;
    const timer = setTimeout(async () => {
      try {
        const searchResult = await searchGuestByPhone(phoneDigits);
        if (companyAutofillRequestRef.current !== requestId) return;

        const guestId = Number(searchResult?.guest?.id);
        if (!Number.isInteger(guestId) || guestId <= 0) {
          setGuestProfileCompanySnapshot(null);
          if (!companyDetailsSavedRef.current) {
            setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
            setCompanyDetailsSaved(false);
            setCompanyErrors({});
          }
          return;
        }

        const customerDetail = await getCustomerDetail(guestId, 1, 1);
        if (companyAutofillRequestRef.current !== requestId) return;

        const customer = customerDetail?.customer || {};
        const normalizedTaxType = String(customer.company_tax_id_type || '').trim().toLowerCase();
        const taxIdType = ['gstin', 'pan', 'tan', 'cin', 'other'].includes(normalizedTaxType)
          ? normalizedTaxType
          : 'gstin';

        const nextCompanyDetails = {
          taxIdType,
          taxIdNumber: String(customer.company_tax_id_number || '').trim(),
          companyName: String(customer.company_name || '').trim(),
          companyPhone: String(customer.company_phone || '').trim(),
          companyEmail: String(customer.company_email || '').trim(),
          companyAddress: String(customer.company_address || '').trim(),
          companyCity: String(customer.company_city || '').trim(),
          companyState: String(customer.company_state || '').trim(),
          companyCountry: String(customer.company_country || '').trim(),
          companyPincode: String(
            customer.company_pincode || customer.company_postal_code || ''
          ).trim(),
        };

        const hasAnyCompanyValue = hasAnyCompanyFieldValues(nextCompanyDetails);

        setGuestProfileCompanySnapshot(hasAnyCompanyValue ? nextCompanyDetails : null);

        if (!companyDetailsSavedRef.current) {
          setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
          setCompanyDetailsSaved(false);
          setCompanyErrors({});
        }
      } catch (error) {
        // Keep the form usable if autofill request fails.
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [guestPhone, guestPhoneCountryCode]);

  const corporateTaxIdOptions = [
    { value: 'gstin', label: 'GSTIN' },
    { value: 'pan', label: 'PAN' },
    { value: 'tan', label: 'TAN' },
    { value: 'cin', label: 'CIN' },
    { value: 'other', label: 'Other' },
  ];

  const companyCountries = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
  const selectedCompanyCountryObj = companyCountries.find(
    (country) =>
      country?.name === companyDetails.companyCountry ||
      country?.code === companyDetails.companyCountry ||
      country?.name?.toLowerCase() === (companyDetails.companyCountry || '').toLowerCase()
  ) || null;
  const companyStates = selectedCompanyCountryObj?.states || [];
  const selectedCompanyStateObj = companyStates.find(
    (state) => state?.name === companyDetails.companyState
  ) || null;
  const companyCities = selectedCompanyStateObj?.cities || [];

  const hasCompanyDetails = companyDetailsSaved && Boolean(companyDetails.companyName?.trim());

  const validateCompanyDetails = () => {
    const nextErrors = {};
    if (!companyDetails.companyName.trim()) {
      nextErrors.companyName = 'Company name is required';
    }
    if (companyDetails.companyEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(companyDetails.companyEmail.trim())) {
        nextErrors.companyEmail = 'Enter a valid company email';
      }
    }
    setCompanyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCompanyDetailsSave = () => {
    if (!validateCompanyDetails()) return;
    setCompanyDetailsSaved(true);
    setCompanyDialogOpen(false);
  };

  const handleOpenCompanyDialog = () => {
    setCompanyErrors({});
    setCompanyDetails((prev) => {
      if (companyDetailsSaved) return prev;
      if (!isCompanyFormEffectivelyEmpty(prev)) return prev;
      if (guestProfileCompanySnapshot) {
        return { ...guestProfileCompanySnapshot };
      }
      return prev;
    });
    setCompanyDialogOpen(true);
  };

  /** Clear corporate / company details for this booking only. Profile snapshot stays for optional re-add. */
  const handleRemoveCompanyDetailsForBooking = () => {
    setCompanyDetails({ ...EMPTY_COMPANY_DETAILS });
    setCompanyDetailsSaved(false);
    setCompanyErrors({});
    setCompanyDialogOpen(false);
  };

  const applyAiGuestFields = useCallback((guest, previews) => {
    const g = guest || {};
    const p =
      typeof previews === 'string'
        ? { frontDataUrl: previews, backDataUrl: null }
        : previews || {};
    const applyTrimmed = (val, setter) => {
      if (val == null || val === '') return;
      const t = String(val).trim();
      if (t !== '') setter(t);
    };
    applyTrimmed(g.guest_title, setGuestTitle);
    applyTrimmed(g.guest_first_name, setGuestFirstName);
    applyTrimmed(g.guest_middle_name, setGuestMiddleName);
    applyTrimmed(g.guest_last_name, setGuestLastName);
    applyTrimmed(g.guest_email, setGuestEmail);
    applyTrimmed(g.guest_address, setGuestAddress);
    applyTrimmed(g.guest_city, setGuestCity);
    applyTrimmed(g.guest_state, setGuestState);
    const countryCombined =
      String(g.guest_country || '').trim() || String(g.nationality || '').trim();
    if (countryCombined) setGuestCountry(countryCombined);
    applyTrimmed(g.guest_pincode, setGuestPincode);
    applyTrimmed(g.guest_dob, setGuestDOB);
    applyTrimmed(g.guest_gender, setGuestGender);
    const docType = g.upload_document_type || 'aadhaar';
    setUploadDocumentType(docType);
    if (g.document_id_number != null && String(g.document_id_number).trim() !== '') {
      setDocumentIdNumber(normalizeDocumentIdInput(docType, g.document_id_number));
    }
    if (p.frontDataUrl) setPassportFrontId(p.frontDataUrl);
    if (p.backDataUrl) setPassportBackId(p.backDataUrl);
  }, []);

  const handleAiCheckinSuccess = useCallback((guest, previews) => {
    setSelectedGuestId(null);
    applyAiGuestFields(guest, previews);
    setAiCheckinOpen(false);
  }, [applyAiGuestFields]);

  // Calculate room prices
  const calculateRoomPrices = useCallback((roomData) => {
    const basePrice = roomData.ratePerNight * roomData.rooms * nights;
    
    // Calculate extra charges: rate per extra guest per night * number of extra guests * nights
    const extraAdultsCount = roomData.extraAdultsCount || 0;
    const extraChildrenCount = roomData.extraChildrenCount || 0;
    const extraAdultChargeRate = roomData.extraAdultCharge || 0;
    const extraChildChargeRate = roomData.extraChildCharge || 0;
    
    const totalExtraAdultCharge = extraAdultChargeRate * extraAdultsCount * nights;
    const totalExtraChildCharge = extraChildChargeRate * extraChildrenCount * nights;
    const extraCharges = totalExtraAdultCharge + totalExtraChildCharge;
    
    const priceWithExtras = basePrice + extraCharges;
    const discount = roomData.discount || 0;
    const priceAfterDiscount = priceWithExtras - discount;
    // GST will be calculated at the total level when enabled
    // So we don't include it in individual room totals
    const total = priceAfterDiscount;

    return {
      nights,
      basePrice,
      extraAdultCharge: totalExtraAdultCharge,
      extraChildCharge: totalExtraChildCharge,
      extraCharges,
      discount,
      priceAfterDiscount,
      total,
    };
  }, [nights]);

  // Note: We removed the useEffect that recalculated all rooms when nights changed
  // Each room now maintains its own prices based on its own check-in/check-out dates
  // This prevents rooms from affecting each other's prices

  // Calculate price breakdown
  const priceBreakdown = React.useMemo(() => {
    let totalBasePrice = 0;

    rooms.forEach(room => {
      // Base price includes room rate * rooms * nights
      const basePrice = room.basePrice || 0;
      
      // Add extra adult and child charges
      const extraAdultCharge = room.extraAdultCharge || 0;
      const extraChildCharge = room.extraChildCharge || 0;
      const extraCharges = extraAdultCharge + extraChildCharge;
      
      // Total price before discount (includes base + extras)
      totalBasePrice += (basePrice + extraCharges);
    });

    const roomLines = rooms.map((room) => {
      const roomCount = Math.max(1, Number(room.rooms || 1));
      const perNightAmount = roomCount > 0 && nights > 0
        ? ((room.basePrice || 0) + (room.extraAdultCharge || 0) + (room.extraChildCharge || 0)) / (roomCount * nights)
        : (room.ratePerNight || 0);
      return {
        ratePerNight: perNightAmount,
        amount: (room.basePrice || 0) + (room.extraAdultCharge || 0) + (room.extraChildCharge || 0),
      };
    });

    return calculateBookingPriceBreakdown({
      roomLines,
      taxRules,
      gstIncluded: effectiveGstIncluded,
      taxInclusive,
      allowDiscount,
      discountMode,
      discountPercentage,
      discountAmount,
      allowExtraCharges,
      extraChargesAmount,
      extraChargesNote,
      totalBasePrice,
      totalRoomBasePrice: totalBasePrice,
      gstPercentage,
      hasTaxRules,
    });
  }, [rooms, nights, allowDiscount, discountMode, discountPercentage, discountAmount, effectiveGstIncluded, taxInclusive, allowExtraCharges, extraChargesAmount, extraChargesNote, gstPercentage, hasTaxRules, taxRules]);

  // Add room handler
  const handleAddRoom = () => {
    const selectedRoomType = availableRoomTypes.find(rt => rt.id.toString() === roomFormData.roomTypeId);
    
    if (!roomFormData.roomTypeId) {
      showAlert('Please select a room type', 'Validation Error', 'warning');
      return;
    }
    if (roomFormData.rooms === 0) {
      showAlert('Please select at least one room', 'Validation Error', 'warning');
      return;
    }
    if (roomFormData.adults === 0 && roomFormData.children === 0) {
      showAlert('Please select at least one adult or child', 'Validation Error', 'warning');
      return;
    }
    if (roomFormData.ratePerNight === 0) {
      showAlert('Please enter room rate per night', 'Validation Error', 'warning');
      return;
    }

    // Check inventory availability - count already added rooms for this room type with overlapping dates
    const roomTypeId = roomFormData.roomTypeId;
    const currentCheckIn = checkInDate.format('YYYY-MM-DD');
    const currentCheckOut = checkOutDate.format('YYYY-MM-DD');
    
    // Helper function to check if date ranges overlap
    const dateRangesOverlap = (start1, end1, start2, end2) => {
      return start1 < end2 && start2 < end1;
    };
    
    // Count only rooms with overlapping dates
    const alreadyAddedCount = rooms
      .filter(r => {
        if (r.roomTypeId !== roomTypeId) return false;
        if (!r.checkInDate || !r.checkOutDate) return false;
        // Check if dates overlap with current selection
        return dateRangesOverlap(r.checkInDate, r.checkOutDate, currentCheckIn, currentCheckOut);
      })
      .reduce((sum, r) => sum + r.rooms, 0);
    
    const totalRequestedRooms = alreadyAddedCount + roomFormData.rooms;
    const availableInventory = selectedRoomType?.available_rooms || 0;
    const originalInventory = selectedRoomType?.original_available_rooms || availableInventory;
    
    if (totalRequestedRooms > originalInventory) {
      const remainingForDates = originalInventory - alreadyAddedCount;
      showAlert(
        `Cannot add ${roomFormData.rooms} room(s). Only ${remainingForDates} room(s) available for ${selectedRoomType?.name || 'this room type'} on ${currentCheckIn} to ${currentCheckOut}. (${alreadyAddedCount} already added for these dates)`,
        'Inventory Limit Exceeded',
        'error'
      );
      return;
    }

    // Calculate extra adults and children counts
    const baseAdults = selectedRoomType?.base_adults || 2;
    const baseChildren = selectedRoomType?.base_children || 0;
    const baseAdultsCapacity = roomFormData.rooms * baseAdults;
    const baseChildrenCapacity = roomFormData.rooms * baseChildren;
    const extraAdultsCount = Math.max(0, roomFormData.adults - baseAdultsCapacity);
    const extraChildrenCount = Math.max(0, roomFormData.children - baseChildrenCapacity);

    // Create room data with extra counts for price calculation
    // Use reservation check-in/check-out dates
    const roomNights =
      bookingType === 'nightly'
        ? getNightlyCalendarNights(checkInDate, checkOutDate)
        : checkOutDate.diff(checkInDate, 'days');
    const effectiveNights = roomNights > 0 ? roomNights : 1;
    
    // Calculate prices for this specific room with its dates
    const basePrice = roomFormData.ratePerNight * roomFormData.rooms * effectiveNights;
    const totalExtraAdultCharge = (roomFormData.extraAdultCharge || 0) * extraAdultsCount * effectiveNights;
    const totalExtraChildCharge = (roomFormData.extraChildCharge || 0) * extraChildrenCount * effectiveNights;
    const extraCharges = totalExtraAdultCharge + totalExtraChildCharge;
    const discount = 0; // Discount is applied at booking level, not per room
    const priceAfterDiscount = basePrice + extraCharges - discount;
    
    const prices = {
      basePrice,
      extraAdultCharge: totalExtraAdultCharge,
      extraChildCharge: totalExtraChildCharge,
      discount,
      priceAfterDiscount,
      total: priceAfterDiscount,
    };
    
    const newRoom = {
      roomType: selectedRoomType?.name || '',
      roomTypeId: roomFormData.roomTypeId,
      mealPlan: roomFormData.mealPlan,
      rooms: roomFormData.rooms,
      adults: roomFormData.adults,
      children: roomFormData.children,
      ratePerNight: roomFormData.ratePerNight,
      extraAdultCharge: roomFormData.extraAdultCharge || 0, // Rate per extra adult per night
      extraChildCharge: roomFormData.extraChildCharge || 0, // Rate per extra child per night
      extraAdultsCount,
      extraChildrenCount,
      baseAdults,
      baseChildren,
      gstIncluded: effectiveGstIncluded,
      taxInclusive: taxInclusive,
      allowDiscount: allowDiscount,
      // Store check-in and check-out dates (from reservation) for display
      checkInDate: checkInDate.format('YYYY-MM-DD'),
      checkOutDate: checkOutDate.format('YYYY-MM-DD'),
      ...prices,
    };

    setRooms([...rooms, newRoom]);
    setRoomDrawerOpen(false);
    setRoomFormData({
      roomTypeId: '',
      mealPlan: 'EP',
      rooms: 0,
      adults: 0,
      children: 0,
      ratePerNight: 0,
      extraAdultCharge: 0,
      extraChildCharge: 0,
    });
  };

  // Remove room handler
  const handleRemoveRoom = (index) => {
    setRooms(rooms.filter((_, i) => i !== index));
  };

  // Step validation
  const validateStep1 = () => {
    if (!checkInDate || !checkOutDate) {
      showAlert('Please select check-in and check-out dates', 'Validation Error', 'warning');
      return false;
    }
    if (checkOutDate <= checkInDate) {
      showAlert('Check-out date must be after check-in date', 'Validation Error', 'warning');
      return false;
    }
    if (rooms.length === 0) {
      showAlert('Please add at least one room type', 'Validation Error', 'warning');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Validation is now handled by GuestDetailsStep component with inline Material Design errors
    // Just check the validation state - no alert dialogs
    return isGuestDetailsValid;
  };

  // Navigation handlers
  const handleNext = () => {
    if (activeStep === STEPS.BOOKING_DETAILS) {
      if (!validateStep1()) return;
      setActiveStep(STEPS.GUEST_DETAILS);
    } else if (activeStep === STEPS.GUEST_DETAILS) {
      // Validate all fields and show errors if invalid
      if (validateGuestDetailsRef.current && typeof validateGuestDetailsRef.current === 'function') {
        const isValid = validateGuestDetailsRef.current();
        if (isValid) {
          submitIdempotencyKeyRef.current = null;
          setActiveStep(STEPS.REVIEW);
        }
        // If invalid, validation errors are shown inline - don't proceed
      } else {
        // If validation function not available, proceed if form appears valid
        if (isGuestDetailsValid) {
          submitIdempotencyKeyRef.current = null;
          setActiveStep(STEPS.REVIEW);
        }
      }
    }
  };

  const handleBack = () => {
    if (activeStep === STEPS.REVIEW) {
      submitIdempotencyKeyRef.current = null;
    }
    setActiveStep(activeStep - 1);
  };

  useEffect(() => {
    if (!isMobileLayout || activeStep !== STEPS.GUEST_DETAILS) return undefined;

    const scrollFrame = requestAnimationFrame(() => {
      const target = guestDetailsTopRef.current || reservationShellRef.current;
      scrollMobileStepTopIntoView(target);
    });

    return () => cancelAnimationFrame(scrollFrame);
  }, [activeStep, isMobileLayout]);

  // Submit handler
  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    // Prepare rooms data - ensure all dates are strings and remove moment objects
    const roomsData = rooms.map(room => {
      // Convert any moment objects to strings and ensure only serializable data
      const roomData = {
        roomTypeId: room.roomTypeId,
        mealPlan: room.mealPlan,
        rooms: room.rooms,
        adults: room.adults,
        children: room.children,
        ratePerNight: room.ratePerNight || 0,
        extraAdultCharge: room.extraAdultCharge || 0,
        extraChildCharge: room.extraChildCharge || 0,
        basePrice: room.basePrice || 0,
        discount: room.discount || 0,
        total: room.total || 0,
      };
      
      // Handle check-in/check-out dates
      if (room.checkInDate) {
        if (moment.isMoment(room.checkInDate)) {
          roomData.checkInDate = room.checkInDate.format('YYYY-MM-DD');
        } else if (typeof room.checkInDate === 'string') {
          roomData.checkInDate = room.checkInDate;
        } else {
          roomData.checkInDate = checkInDate.format('YYYY-MM-DD');
        }
      } else {
        roomData.checkInDate = checkInDate.format('YYYY-MM-DD');
      }
      
      if (room.checkOutDate) {
        if (moment.isMoment(room.checkOutDate)) {
          roomData.checkOutDate = room.checkOutDate.format('YYYY-MM-DD');
        } else if (typeof room.checkOutDate === 'string') {
          roomData.checkOutDate = room.checkOutDate;
        } else {
          roomData.checkOutDate = checkOutDate.format('YYYY-MM-DD');
        }
      } else {
        roomData.checkOutDate = checkOutDate.format('YYYY-MM-DD');
      }
      
      return roomData;
    });
    
    if (!submitIdempotencyKeyRef.current) {
      submitIdempotencyKeyRef.current = generateIdempotencyKey();
    }

    // Reservation "No Document" uses sentinel value in UI; submit empty document_type/id_number.
    const normalizedUploadDocumentType = uploadDocumentType === 'no_document' ? '' : (uploadDocumentType || '');

    const formData = {
      checkInDate: checkInDate.format('YYYY-MM-DD'),
      checkOutDate: checkOutDate.format('YYYY-MM-DD'),
      nights: nights,
      bookingType: bookingType,
      rooms: roomsData,
      guest: {
        ...(selectedGuestId ? { id: selectedGuestId } : {}),
        title: guestTitle || '',
        first_name: guestFirstName.trim(),
        middle_name: guestMiddleName.trim() || '',
        last_name: guestLastName.trim(),
        email: guestEmail.trim() || '',
        phone: (guestPhone || '').trim().replace(/\D/g, ''),
        country_code: guestPhoneCountryCode || '+91',
        alternate_phone: (guestAlternatePhone || '').trim().replace(/\D/g, '') || '',
        alternate_phone_country_code: guestAlternatePhoneCountryCode || '+91',
        address_line1: guestAddress.trim() || '',
        city: guestCity.trim() || '',
        state: guestState.trim() || '',
        country: (() => {
          // Convert country name to code if needed
          if (!guestCountry) return '';
          // Try to find country code from countries data
          const countryObj = countriesData.countries.find(c => c.name === guestCountry || c.code === guestCountry);
          return countryObj ? countryObj.code : guestCountry.trim();
        })(),
        pincode: guestPincode.trim() || '',
        dob: guestDOB || '',
        gender: guestGender || '',
        purpose_of_stay: purposeOfStay || '',
        purpose_of_stay_note:
          purposeOfStay === 'other' ? (purposeOfStayNote || '').trim() : '',
        eta: guestETA || '',
        guest_note: guestNote || '',
        booking_reference: bookingReference || '',
        booking_source: hasCompanyDetails ? 'corporate' : (bookingSource || ''),
        remarks: remarks || '',
        company_name: hasCompanyDetails ? (companyDetails.companyName || '') : '',
        company_tax_id_type: hasCompanyDetails ? (companyDetails.taxIdType || '') : '',
        company_tax_id_number: hasCompanyDetails ? (companyDetails.taxIdNumber || '') : '',
        company_phone: hasCompanyDetails ? (companyDetails.companyPhone || '') : '',
        company_email: hasCompanyDetails ? (companyDetails.companyEmail || '') : '',
        company_address: hasCompanyDetails ? (companyDetails.companyAddress || '') : '',
        company_city: hasCompanyDetails ? (companyDetails.companyCity || '') : '',
        company_state: hasCompanyDetails ? (companyDetails.companyState || '') : '',
        company_country: hasCompanyDetails ? (companyDetails.companyCountry || '') : '',
        company_pincode: hasCompanyDetails ? (companyDetails.companyPincode || '') : '',
        company_postal_code: hasCompanyDetails ? (companyDetails.companyPincode || '') : '',
        document_type: normalizedUploadDocumentType,
        id_number: normalizeDocumentIdForSubmit(normalizedUploadDocumentType, documentIdNumber) || '',
        id_front: passportFrontId || '',
        id_back: passportBackId || '',
        ...(isForeignGuestCountry(guestCountry, hotelCountry)
          ? foreignDetailsToGuestApiPayload(foreignGuestDetails)
          : {}),
      },
      // Booking settings
      gstIncluded: effectiveGstIncluded,
      taxInclusive: taxInclusive,
      allowDiscount: allowDiscount,
      discountPercentage: allowDiscount && discountMode === 'percentage' ? discountPercentage : 0,
      discountAmount: allowDiscount && discountMode === 'amount' ? discountAmount : 0,
      allowExtraCharges,
      extraChargesAmount: allowExtraCharges ? extraChargesAmount : 0,
      extraChargesNote: allowExtraCharges ? extraChargesNote.trim() : '',
      // Collect advance data
      collectAdvance: collectAdvance,
      advanceAmount: advanceAmount || 0,
      collectionDate: toPaymentProcessedAtIso(collectionDate) || '',
      paymentMethod: paymentMethod || '',
      referenceNumber: referenceNumber || '',
      advanceDocument: advanceDocument || '',
      idempotencyKey: submitIdempotencyKeyRef.current,
    };

    setIsSubmitting(true);
    try {
      const response = await submitReservation(formData);
      // Store booking data for success dialog
      setBookingData(response);
      setActiveBookingSlide(0); // Reset slider to first booking
      const bidList = [];
      if (Array.isArray(response.bookings) && response.bookings.length > 0) {
        response.bookings.forEach((booking) => {
          const idCandidate = booking?.booking_id ?? booking?.id ?? null;
          const parsedId = Number(idCandidate);
          if (Number.isInteger(parsedId) && parsedId > 0) bidList.push(parsedId);
        });
      }
      const uniqIds = [...new Set(bidList)];
      const firstPlId = uniqIds.length
        ? uniqIds[0]
        : Number(response.booking_id ?? response.id ?? 0);
      const plId = Number.isInteger(firstPlId) && firstPlId > 0 ? firstPlId : 0;
      const dueForLink = computeAmountDueForPaymentLink({
        total: priceBreakdown?.total,
        paymentMethod,
        collectAdvance,
        advanceAmount,
        isWalkIn: false,
      });
      setSuccessPaymentLinkBookingId(plId);
      setSuccessPaymentLinkAmount(dueForLink);
      setShowPaymentLinkOnSuccess(plId > 0 && dueForLink > 0 && canSendPaymentLink);
      setSuccessDialogOpen(true);
    } catch (error) {
      // Extract error message from response
      let errorMsg = 'Error submitting reservation. Please try again.';
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };
  
  const handleCloseSuccessDialog = () => {
    submitIdempotencyKeyRef.current = null;
    setSuccessDialogOpen(false);
    setSuccessActionLoading(null);
    setShowPaymentLinkOnSuccess(false);
    setSuccessPaymentLinkBookingId(0);
    setSuccessPaymentLinkAmount(0);
    // Redirect to booking home after closing the dialog
    navigate('/booking/');
  };

  const getActionBookingIds = () => {
    if (!bookingData) {
      return [];
    }

    const bookingIds = [];
    if (Array.isArray(bookingData.bookings) && bookingData.bookings.length > 0) {
      bookingData.bookings.forEach((booking) => {
        const idCandidate = booking?.booking_id ?? booking?.id ?? null;
        const parsedId = Number(idCandidate);
        if (Number.isInteger(parsedId) && parsedId > 0) {
          bookingIds.push(parsedId);
        }
      });
    }

    if (bookingIds.length > 0) {
      return [...new Set(bookingIds)];
    }

    const fallbackId = Number(bookingData.booking_id ?? bookingData.id ?? null);
    if (Number.isInteger(fallbackId) && fallbackId > 0) {
      return [fallbackId];
    }

    return [];
  };

  const getGroupBookingInfo = () => {
    if (!bookingData) return null;
    const group =
      bookingData.group_booking
      || (bookingData.group_booking_code
        ? {
            group_booking_code: bookingData.group_booking_code,
            group_booking_id: bookingData.group_booking_id,
          }
        : null);
    if (!group) return null;
    const code = group.group_booking_code || group.code || bookingData.group_booking_code || null;
    if (!code) return null;
    return {
      ...group,
      group_booking_code: code,
      total_amount: group.total_amount ?? group.totalAmount ?? null,
      booking_count:
        group.booking_count
        ?? (Array.isArray(bookingData.bookings) ? bookingData.bookings.length : null),
    };
  };

  const getInvoiceActionBookingIds = () => {
    const ids = getActionBookingIds();
    // Group bookings share one combined invoice — download/send once via any member.
    if (getGroupBookingInfo() && ids.length > 1) {
      return [ids[0]];
    }
    return ids;
  };

  const getCurrentBooking = () => {
    if (!bookingData) {
      return null;
    }
    if (Array.isArray(bookingData.bookings) && bookingData.bookings.length > 0) {
      return bookingData.bookings[activeBookingSlide] || bookingData.bookings[0];
    }
    return bookingData;
  };

  // Get booking code for display/actions
  const getBookingCode = () => {
    const group = getGroupBookingInfo();
    if (group?.group_booking_code) {
      return group.group_booking_code;
    }
    const booking = getCurrentBooking();
    if (!booking) return null;
    return booking.booking_code || booking.code || booking.booking_id || booking.id || null;
  };

  const extractActionErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  const handleDownloadInvoice = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for invoice download', 'Warning', 'warning');
      return;
    }

    const group = getGroupBookingInfo();
    setSuccessActionLoading('invoice');
    try {
      for (const bookingId of bookingIds) {
        await downloadBookingInvoice(bookingId, 'hotel', { groupInvoice: Boolean(group) });
      }
      showAlert(
        group
          ? `Group invoice downloaded${group.group_booking_code ? ` (${group.group_booking_code})` : ''}`
          : bookingIds.length > 1
            ? `Invoices downloaded for ${bookingIds.length} bookings`
            : 'Invoice downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Invoice download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleDownloadVoucher = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    if (!bookingIds.length) {
      showAlert('Booking details not found for voucher download', 'Warning', 'warning');
      return;
    }

    const group = getGroupBookingInfo();
    setSuccessActionLoading('voucher');
    try {
      for (const bookingId of bookingIds) {
        await downloadBookingConfirmation(bookingId, { groupVoucher: Boolean(group) });
      }
      showAlert(
        group
          ? `Group voucher downloaded${group.group_booking_code ? ` (${group.group_booking_code})` : ''}`
          : bookingIds.length > 1
            ? `Vouchers downloaded for ${bookingIds.length} bookings`
            : 'Voucher downloaded successfully',
        'Success',
        'success'
      );
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Voucher download failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleSendWhatsApp = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    const phoneNumber = guestPhone?.replace(/\D/g, '');
    const group = getGroupBookingInfo();

    if (!bookingIds.length) {
      showAlert('Booking details not found for sending WhatsApp', 'Warning', 'warning');
      return;
    }
    if (!phoneNumber) {
      showAlert('Phone number is required to send WhatsApp message', 'Warning', 'warning');
      return;
    }

    setSuccessActionLoading('whatsapp');
    try {
      let sentCount = 0;
      const errors = [];
      let lastRes = null;

      for (const bookingId of bookingIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: false,
          send_whatsapp: true,
          groupInvoice: Boolean(group),
          groupVoucher: Boolean(group),
        });
        lastRes = res;
        if (res?.whatsapp_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }

      if (sentCount > 0) {
        const sentDocs =
          Array.isArray(lastRes?.whatsapp_sent_documents) && lastRes.whatsapp_sent_documents.length
            ? lastRes.whatsapp_sent_documents.join(' and ')
            : group
              ? 'group voucher and invoice'
              : 'voucher and invoice';
        showAlert(
          formatWhatsAppSuccessMessage(lastRes, {
            prefix: group
              ? `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp (${group.group_booking_code || 'group'})`
              : `${sentDocs.charAt(0).toUpperCase()}${sentDocs.slice(1)} sent on WhatsApp`,
            bookingCode: getBookingCode(),
          }),
          'Success',
          'success'
        );
      } else {
        showAlert(errors[0] || 'WhatsApp not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'WhatsApp send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const handleSendEmail = async () => {
    const bookingIds = getInvoiceActionBookingIds();
    const email = guestEmail;
    const group = getGroupBookingInfo();

    if (!bookingIds.length) {
      showAlert('Booking details not found for sending email', 'Warning', 'warning');
      return;
    }
    if (!email) {
      showAlert('Email address is required to send email', 'Warning', 'warning');
      return;
    }

    setSuccessActionLoading('email');
    try {
      let sentCount = 0;
      const errors = [];

      for (const bookingId of bookingIds) {
        const res = await sendBookingConfirmation(bookingId, {
          send_email: true,
          send_whatsapp: false,
          groupInvoice: Boolean(group),
          groupVoucher: Boolean(group),
        });
        if (res?.email_sent) {
          sentCount += 1;
        } else if (Array.isArray(res?.errors) && res.errors.length > 0) {
          errors.push(...res.errors);
        }
      }

      if (sentCount > 0) {
        const successMessage = group
          ? `Group voucher and invoice sent to guest email${
              group.group_booking_code ? ` (${group.group_booking_code})` : ''
            }`
          : bookingIds.length > 1
            ? `Confirmation and invoice sent by email for ${sentCount}/${bookingIds.length} bookings`
            : 'Confirmation and invoice sent to guest email';
        showAlert(successMessage, 'Success', 'success');
      } else {
        showAlert(errors[0] || 'Email not sent', 'Warning', 'warning');
      }
    } catch (error) {
      showAlert(extractActionErrorMessage(error, 'Email send failed'), 'Error', 'error');
    } finally {
      setSuccessActionLoading(null);
    }
  };

  const stepLabels = [
    { label: 'Booking', shortLabel: 'Booking', description: 'Select dates and rooms' },
    { label: 'Guest Details', shortLabel: 'Guest', description: 'Guest information' },
    { label: 'Review', shortLabel: 'Review', description: 'Review and confirm' },
  ];

  const isDark = resolveDashboardThemeMode(currentTheme) === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const selectedCurrency = getSelectedHotelCurrency();
  const selectedCurrencySymbol = getBookingCurrencySymbol(selectedCurrency);
  const successGroupBooking = getGroupBookingInfo();
  const companyDrawerOverlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box ref={reservationShellRef} sx={isMobileLayout ? getReservationShellSx(currentTheme) : undefined}>
        {isMobileLayout && (
          <>
            <Box sx={{ mb: 1 }}>
              <DashboardHeader
                title="Reservation"
                forceDark
                navLinks={[
                  { url: '/booking/dashboard/', label: 'Booking Dashboard' },
                  { url: '/booking/list/', label: 'Booking List' },
                  { url: '/booking/walk-in/', label: 'Walk-In' },
                ]}
              />
            </Box>
            <WalkInMobileStepBar
              steps={stepLabels}
              activeStep={activeStep}
              stepIcons={RESERVATION_STEP_ICONS}
            />
          </>
        )}

        <Paper elevation={0} sx={getReservationOuterPaperSx(isDark, isMobileLayout)}>
          <Grid container spacing={isMobileLayout ? 0 : 3}>
        {/* Left Sidebar - Steps (desktop only) */}
        {!isMobileLayout && (
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 20,
              backgroundColor: isDark ? '#232329' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              p: 3,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                mb: 3,
                fontWeight: 600,
              }}
            >
              Reservation
            </Typography>
            <Stepper 
              activeStep={activeStep} 
              orientation="vertical"
              sx={{ 
                '& .MuiStepLabel-label': {
                  color: isDark ? '#999' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  '&.Mui-active': {
                    color: '#4ce1b6',
                    fontWeight: 600,
                  },
                  '&.Mui-completed': {
                    color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  },
                },
                '& .MuiStepIcon-root': {
                  color: isDark ? '#333246' : '#e0e0e0',
                  '&.Mui-active': {
                    color: '#4ce1b6',
                  },
                  '&.Mui-completed': {
                    color: '#4ce1b6',
                  },
                },
                '& .MuiStepConnector-root': {
                  '& .MuiStepConnector-line': {
                    borderColor: isDark ? '#333246' : '#e0e0e0',
                  },
                },
              }}
            >
              {stepLabels.map((step, index) => (
                <Step key={step.label} active={index === activeStep} completed={index < activeStep}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Grid>
        )}

        {/* Form Content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={getReservationFormPaperSx(isDark, isMobileLayout)}
          >
            <Box>
              {activeStep === STEPS.BOOKING_DETAILS && (
                <BookingDetailsStep
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  onCheckInDateChange={handleCheckInDateChange}
                  onCheckOutDateChange={setCheckOutDate}
                  nights={nights}
                  rooms={rooms}
                  availableRoomTypes={filteredRoomTypes}
                  loadingRoomTypes={loadingRoomTypes}
                  onAddRoom={handleAddRoom}
                  onRemoveRoom={handleRemoveRoom}
                  roomDrawerOpen={roomDrawerOpen}
                  onRoomDrawerOpen={() => setRoomDrawerOpen(true)}
                  onRoomDrawerClose={() => setRoomDrawerOpen(false)}
                  roomFormData={roomFormData}
                  onRoomFormDataChange={setRoomFormData}
                  bookingType={bookingType}
                  onBookingTypeChange={setBookingType}
                />
              )}

              {activeStep === STEPS.GUEST_DETAILS && (
                <Box ref={guestDetailsTopRef} sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: aiCheckinOpen ? 'blur(10px)' : 'none',
                      opacity: aiCheckinOpen ? 0.55 : 1,
                      transition: 'filter 0.35s ease, opacity 0.35s ease',
                      pointerEvents: aiCheckinOpen ? 'none' : 'auto',
                      userSelect: aiCheckinOpen ? 'none' : 'auto',
                    }}
                  >
                    <GuestDetailsStep
                  guestTitle={guestTitle}
                  guestFirstName={guestFirstName}
                  guestMiddleName={guestMiddleName}
                  guestLastName={guestLastName}
                  onGuestTitleChange={setGuestTitle}
                  onGuestFirstNameChange={setGuestFirstName}
                  onGuestMiddleNameChange={setGuestMiddleName}
                  onGuestLastNameChange={setGuestLastName}
                  guestEmail={guestEmail}
                  guestPhone={guestPhone}
                  guestPhoneCountryCode={guestPhoneCountryCode}
                  guestAlternatePhone={guestAlternatePhone}
                  guestAlternatePhoneCountryCode={guestAlternatePhoneCountryCode}
                  onGuestEmailChange={setGuestEmail}
                  onGuestPhoneChange={setGuestPhone}
                  onGuestPhoneCountryCodeChange={handleGuestPhoneCountryCodeChange}
                  onSelectedGuestIdChange={setSelectedGuestId}
                  selectedGuestId={selectedGuestId}
                  onGuestAlternatePhoneChange={setGuestAlternatePhone}
                  onGuestAlternatePhoneCountryCodeChange={handleGuestAlternatePhoneCountryCodeChange}
                  guestAddress={guestAddress}
                  guestCity={guestCity}
                  guestState={guestState}
                  guestCountry={guestCountry}
                  guestPincode={guestPincode}
                  onGuestAddressChange={setGuestAddress}
                  onGuestCityChange={setGuestCity}
                  onGuestStateChange={setGuestState}
                  onGuestCountryChange={setGuestCountry}
                  onGuestPincodeChange={setGuestPincode}
                  guestDOB={guestDOB}
                  guestGender={guestGender}
                  onGuestDOBChange={setGuestDOB}
                  onGuestGenderChange={setGuestGender}
                  guestETA={guestETA}
                  guestNote={guestNote}
                  bookingReference={bookingReference}
                  bookingSource={bookingSource}
                  remarks={remarks}
                  onGuestETAChange={setGuestETA}
                  onGuestNoteChange={setGuestNote}
                  onBookingReferenceChange={setBookingReference}
                  onBookingSourceChange={setBookingSource}
                  uploadDocumentType={uploadDocumentType}
                  onUploadDocumentChange={setUploadDocumentType}
                  documentIdNumber={documentIdNumber}
                  onDocumentIdNumberChange={setDocumentIdNumber}
                  onRemarksChange={setRemarks}
                  purposeOfStay={purposeOfStay}
                  purposeOfStayNote={purposeOfStayNote}
                  onPurposeOfStayChange={setPurposeOfStay}
                  onPurposeOfStayNoteChange={setPurposeOfStayNote}
                  passportFrontId={passportFrontId}
                  passportBackId={passportBackId}
                  onPassportFrontIdChange={setPassportFrontId}
                  onPassportBackIdChange={setPassportBackId}
                  hotelCountry={hotelCountry}
                  foreignGuestRoomNumber={rooms[0]?.roomNumber || rooms[0]?.allocatedRoomNumber || ''}
                  foreignGuestCheckInDate={checkInDate}
                  foreignGuestDetails={foreignGuestDetails}
                  onForeignGuestDetailsChange={setForeignGuestDetails}
                  allowNoDocument={true}
                  hideGuestInformationHeading={isMobileLayout}
                  onValidationChange={setIsGuestDetailsValid}
                  onValidateRequest={(fn) => { validateGuestDetailsRef.current = fn; }}
                  headingActions={
                    <Box sx={getReservationHeadingActionsSx(isMobileLayout)}>
                      {canManageAiCheckin && (
                        <AiGradientButton onClick={() => setAiCheckinOpen(true)}>
                          AI Check-in
                        </AiGradientButton>
                      )}
                      <CompanyDetailsHeadingActions
                        hasCompanyDetails={hasCompanyDetails}
                        companyName={companyDetails.companyName}
                        isDark={isDark}
                        onEdit={handleOpenCompanyDialog}
                        onRemove={handleRemoveCompanyDetailsForBooking}
                      />
                    </Box>
                  }
                    />
                  </Box>
                  {canManageAiCheckin && (
                    <WalkInAiCheckinOverlay
                      open={aiCheckinOpen}
                      onClose={() => setAiCheckinOpen(false)}
                      onSuccess={handleAiCheckinSuccess}
                      isDark={isDark}
                    />
                  )}
                </Box>
              )}

              {activeStep === STEPS.REVIEW && (
                <ReviewStep
                  checkInDate={checkInDate}
                  checkOutDate={checkOutDate}
                  nights={nights}
                  rooms={rooms}
                  guestName={`${guestFirstName} ${guestLastName}`.trim()}
                  guestEmail={guestEmail}
                  guestPhone={guestPhone}
                  guestAddress={guestAddress}
                  gstIncluded={effectiveGstIncluded}
                  taxInclusive={taxInclusive}
                  allowDiscount={allowDiscount}
                  discountMode={discountMode}
                  discountPercentage={discountPercentage}
                  discountAmount={discountAmount}
                  maxDiscountAmount={priceBreakdown?.priceBeforeDiscount || 0}
                  bookingType={bookingType}
                  autoGstEnabled={autoGstEnabled}
                  onGstIncludedChange={setGstIncluded}
                  onTaxInclusiveChange={setTaxInclusive}
                  onAllowDiscountChange={setAllowDiscount}
                  onDiscountModeChange={setDiscountMode}
                  onDiscountPercentageChange={setDiscountPercentage}
                  onDiscountAmountChange={setDiscountAmount}
                  allowExtraCharges={allowExtraCharges}
                  extraChargesAmount={extraChargesAmount}
                  extraChargesNote={extraChargesNote}
                  extraChargesItems={extraChargesItems}
                  extraChargesBaseAmount={extraChargesBaseAmount}
                  extraChargesGstAmount={extraChargesGstAmount}
                  onAllowExtraChargesChange={setAllowExtraCharges}
                  onExtraChargesAmountChange={setExtraChargesAmount}
                  onExtraChargesNoteChange={setExtraChargesNote}
                  onExtraChargesItemsChange={setExtraChargesItems}
                  onExtraChargesBaseAmountChange={setExtraChargesBaseAmount}
                  onExtraChargesGstAmountChange={setExtraChargesGstAmount}
                  priceBreakdown={priceBreakdown}
                  isWalkIn={false}
                  hasTaxRules={hasTaxRules}
                  collectAdvance={collectAdvance}
                  advanceAmount={advanceAmount}
                  collectionDate={collectionDate}
                  paymentMethod={paymentMethod}
                  referenceNumber={referenceNumber}
                  advanceDocument={advanceDocument}
                  onCollectAdvanceChange={setCollectAdvance}
                  onAdvanceAmountChange={setAdvanceAmount}
                  onCollectionDateChange={setCollectionDate}
                  onPaymentMethodChange={setPaymentMethod}
                  onReferenceNumberChange={setReferenceNumber}
                  onAdvanceDocumentChange={setAdvanceDocument}
                  suppressPaymentLinkOverlay
                />
              )}
            </Box>

            <Box sx={getReservationFooterSx(isDark, isMobileLayout)}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                size={isMobileLayout ? 'medium' : 'large'}
                sx={getReservationFooterButtonSx('back', isDark, isMobileLayout)}
              >
                Back
              </Button>
              {activeStep === stepLabels.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  size={isMobileLayout ? 'medium' : 'large'}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#000' }} /> : null}
                  sx={getReservationFooterButtonSx('next', isDark, isMobileLayout)}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Reservation'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size={isMobileLayout ? 'medium' : 'large'}
                  sx={getReservationFooterButtonSx('next', isDark, isMobileLayout)}
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
        </Paper>
      </Box>

      {/* Company Details Drawer */}
      <Drawer
        anchor="right"
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, { width: BOOKING_DRAWER_WIDTH_SX }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Company details
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCompanyDialogOpen(false)}
              sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...companyDrawerOverlays.fieldShellSx }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Tax ID Type"
                      value={companyDetails.taxIdType}
                      onChange={(e) => setCompanyDetails((prev) => ({ ...prev, taxIdType: e.target.value }))}
                      SelectProps={{ MenuProps: companyDrawerOverlays.selectMenuProps }}
                    >
                      {corporateTaxIdOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Enter Tax Identification Number"
                      value={companyDetails.taxIdNumber}
                      onChange={(e) => setCompanyDetails((prev) => ({ ...prev, taxIdNumber: e.target.value }))}
                      error={Boolean(companyErrors.taxIdNumber)}
                      helperText={companyErrors.taxIdNumber || ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyDetails.companyName}
                  onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyName: e.target.value }))}
                  error={Boolean(companyErrors.companyName)}
                  helperText={companyErrors.companyName || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Phone"
                  value={companyDetails.companyPhone}
                  onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyPhone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Email"
                  value={companyDetails.companyEmail}
                  onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyEmail: e.target.value }))}
                  error={Boolean(companyErrors.companyEmail)}
                  helperText={companyErrors.companyEmail || ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={companyDetails.companyAddress}
                  onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyAddress: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyCountries}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={countryFilterOptions}
                  slotProps={companyDrawerOverlays.autocompleteSlotProps}
                  value={selectedCompanyCountryObj}
                  onChange={(event, newValue) => {
                    const countryName = newValue?.name || '';
                    setCompanyDetails((prev) => ({
                      ...prev,
                      companyCountry: countryName,
                      companyState: '',
                      companyCity: '',
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="Country" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyStates}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={stateFilterOptions}
                  slotProps={companyDrawerOverlays.autocompleteSlotProps}
                  value={selectedCompanyStateObj}
                  onChange={(event, newValue) => {
                    const stateName = newValue?.name || '';
                    setCompanyDetails((prev) => ({
                      ...prev,
                      companyState: stateName,
                      companyCity: '',
                    }));
                  }}
                  disabled={!selectedCompanyCountryObj}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="State" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={companyCities}
                  getOptionLabel={(option) => option || ''}
                  filterOptions={cityFilterOptions}
                  slotProps={companyDrawerOverlays.autocompleteSlotProps}
                  value={companyCities.find((city) => city === companyDetails.companyCity) || null}
                  onChange={(event, newValue) => {
                    setCompanyDetails((prev) => ({
                      ...prev,
                      companyCity: newValue || '',
                    }));
                  }}
                  disabled={!selectedCompanyStateObj}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="City" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={companyDetails.companyPincode}
                  onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyPincode: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  Optional: add company details for corporate invoices and booking source &quot;Corporate&quot;.
                  Skip this for a normal reservation; saved profile data loads here only when you open this form.
                </Alert>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Button
              onClick={() => setCompanyDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompanyDetailsSave}
              variant="contained"
              fullWidth
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              Add Company Detail
            </Button>
          </Box>
        </Box>
      </Drawer>
      
      {/* Success Dialog - Creative 3D Design - Same as Walk-In */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
            background: isDark
              ? drawerTheme.surfaceGradient
              : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
            border: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            boxShadow: isDark 
              ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.1)'
              : '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(76, 225, 182, 0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, #4ce1b6 0%, #3ab896 50%, #4ce1b6 100%)`,
              boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            pt: 1.75,
            px: 2,
            borderBottom: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            background: isDark 
              ? `linear-gradient(135deg, rgba(76, 225, 182, 0.05) 0%, rgba(58, 184, 150, 0.05) 100%)`
              : `linear-gradient(135deg, rgba(76, 225, 182, 0.03) 0%, rgba(58, 184, 150, 0.03) 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: `linear-gradient(135deg, #4ce1b6 0%, #3ab896 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(76, 225, 182, 0.4)',
              }}
            >
              <CheckCircleIcon sx={{ color: '#000', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 700, fontSize: '0.95rem' }}>
              {successGroupBooking
                ? `Group Booking Confirmed! (${
                    successGroupBooking.booking_count
                    || bookingData?.bookings?.length
                    || 0
                  })`
                : bookingData?.bookings && bookingData.bookings.length > 1
                  ? `Bookings Confirmed! (${bookingData.bookings.length})`
                  : 'Booking Confirmed!'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseSuccessDialog} 
            size="small" 
            sx={{ 
              color: isDark ? drawerTheme.subtleText : '#666',
              '&:hover': {
                backgroundColor: isDark ? drawerTheme.chipBg : '#f5f5f5',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          {bookingData && (
            <Box>
              {bookingData.bookings && Array.isArray(bookingData.bookings) && bookingData.bookings.length > 1 ? (
                <Box>
                  {successGroupBooking?.group_booking_code ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.1,
                        mb: 1,
                        borderRadius: 1.5,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.5)',
                          mb: 0.25,
                        }}
                      >
                        Group booking
                      </Typography>
                      <Typography sx={{ color: '#4ce1b6', fontWeight: 800, fontSize: '0.95rem' }}>
                        {successGroupBooking.group_booking_code}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: '0.68rem',
                          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.55)',
                        }}
                      >
                        One invoice covers all {bookingData.bookings.length} member bookings
                      </Typography>
                    </Paper>
                  ) : null}

                  {bookingData.bookings[activeBookingSlide] && (() => {
                    const booking = bookingData.bookings[activeBookingSlide];
                    const roomData = rooms[activeBookingSlide] || {};
                    return (
                      <BookingConfirmedSummaryCard
                        key={`booking-slide-${activeBookingSlide}`}
                        isDark={isDark}
                        badgeLabel={`${activeBookingSlide + 1} / ${bookingData.bookings.length}`}
                        bookingCode={booking.booking_code || booking.code || booking.booking_id || booking.id}
                        groupBookingCode={successGroupBooking?.group_booking_code || null}
                        codeLabel="Member booking"
                        checkInDate={booking.check_in_date || roomData.checkInDate || checkInDate}
                        checkOutDate={booking.check_out_date || roomData.checkOutDate || checkOutDate}
                        guestName={`${guestFirstName || ''} ${guestLastName || ''}`.trim()}
                        guestPhone={guestPhone}
                        roomsCount={booking.total_rooms || roomData.rooms || 1}
                        adults={booking.adults ?? roomData.adults}
                        children={booking.children ?? roomData.children ?? 0}
                        amount={toCurrencyAmount(booking.total_amount || roomData.total)}
                        currency={booking.currency || selectedCurrency}
                        allocatedRooms={booking.allocated_rooms || []}
                        roomTypeName={booking.room_type_name || roomData.roomType || ''}
                        formRooms={roomData.roomType ? [roomData] : rooms}
                      />
                    );
                  })()}

                  <MobileStepper
                    variant="dots"
                    steps={bookingData.bookings.length}
                    position="static"
                    activeStep={activeBookingSlide}
                    sx={{
                      mt: 0.5,
                      mb: 1,
                      backgroundColor: 'transparent',
                      '& .MuiMobileStepper-dot': {
                        backgroundColor: isDark ? '#444' : '#ccc',
                      },
                      '& .MuiMobileStepper-dotActive': {
                        backgroundColor: '#4ce1b6',
                      },
                    }}
                    nextButton={
                      <Button
                        size="small"
                        onClick={() => setActiveBookingSlide((prev) => Math.min(prev + 1, bookingData.bookings.length - 1))}
                        disabled={activeBookingSlide === bookingData.bookings.length - 1}
                        sx={{
                          color: '#4ce1b6',
                          '&.Mui-disabled': {
                            color: isDark ? '#666' : '#999',
                          },
                        }}
                      >
                        Next
                        <KeyboardArrowRight />
                      </Button>
                    }
                    backButton={
                      <Button
                        size="small"
                        onClick={() => setActiveBookingSlide((prev) => Math.max(prev - 1, 0))}
                        disabled={activeBookingSlide === 0}
                        sx={{
                          color: '#4ce1b6',
                          '&.Mui-disabled': {
                            color: isDark ? '#666' : '#999',
                          },
                        }}
                      >
                        <KeyboardArrowLeft />
                        Back
                      </Button>
                    }
                  />

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.1,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 700, fontSize: '0.8rem' }}>
                        {successGroupBooking
                          ? 'Total (group booking)'
                          : 'Total (all bookings)'}
                      </Typography>
                      <Typography sx={{ color: '#4ce1b6', fontWeight: 800, fontSize: '1.05rem' }}>
                        {selectedCurrencySymbol}
                        {toCurrencyAmount(
                          successGroupBooking?.total_amount ?? priceBreakdown.total
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              ) : (
                (() => {
                  const booking = bookingData.bookings?.[0] || bookingData;
                  return (
                    <BookingConfirmedSummaryCard
                      isDark={isDark}
                      bookingCode={getBookingCode()}
                      checkInDate={booking.check_in_date || checkInDate}
                      checkOutDate={booking.check_out_date || checkOutDate}
                      guestName={`${guestFirstName || ''} ${guestLastName || ''}`.trim()}
                      guestPhone={guestPhone}
                      roomsCount={
                        booking.total_rooms
                        || rooms.reduce((sum, room) => sum + (room.rooms || 0), 0)
                      }
                      adults={
                        booking.adults
                        ?? rooms.reduce((sum, room) => sum + (room.adults || 0), 0)
                      }
                      children={
                        booking.children
                        ?? rooms.reduce((sum, room) => sum + (room.children || 0), 0)
                      }
                      amount={toCurrencyAmount(booking.total_amount || priceBreakdown.total)}
                      currency={booking.currency || selectedCurrency}
                      allocatedRooms={
                        booking.allocated_rooms
                        || bookingData.allocated_rooms
                        || []
                      }
                      roomTypeName={booking.room_type_name || ''}
                      formRooms={rooms}
                    />
                  );
                })()
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 1.75,
            pt: 0.75,
            flexDirection: 'column',
            gap: 0,
            alignItems: 'stretch',
            borderTop: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
          }}
        >
          <BookingConfirmedActionsContent
            isDark={isDark}
            accent="#4ce1b6"
            successActionLoading={successActionLoading}
            onDownloadInvoice={handleDownloadInvoice}
            onDownloadVoucher={handleDownloadVoucher}
            onSendWhatsApp={handleSendWhatsApp}
            onSendEmail={handleSendEmail}
            onDone={handleCloseSuccessDialog}
            invoiceButtonLabel={
              successGroupBooking ? 'Download group invoice' : 'Download invoice'
            }
            voucherButtonLabel={
              successGroupBooking ? 'Download group voucher' : 'Download voucher'
            }
            showPaymentLink={showPaymentLinkOnSuccess}
            paymentLinkBookingId={successPaymentLinkBookingId}
            paymentLinkBooking={successPaymentLinkBooking}
            paymentLinkAmount={successPaymentLinkAmount}
            paymentLinkCurrency={successPaymentLinkBooking?.currency || selectedCurrency}
            canSendPaymentLinkEmail={canSendPaymentLinkEmail}
            canSendPaymentLinkWhatsApp={canSendPaymentLinkWhatsApp}
            onPaymentLinkSent={(msg) => showAlert(msg, 'Success', 'success')}
          />
        </DialogActions>
      </Dialog>
      
      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
            background: isDark ? drawerTheme.surfaceGradient : '#ffffff',
            border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#f44336', fontSize: 32 }} />
            <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
              Error
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseErrorDialog}
            size="small"
            sx={{ color: isDark ? '#999' : '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: isDark ? '#ddd' : '#333', mt: 1 }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCloseErrorDialog}
            variant="contained"
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialogOpen}
        onClose={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />
    </LocalizationProvider>
  );
};

export default ReservationForm;

